import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ProductsService } from '../firebase';
import { Product } from '../types/product';
import OptimizedImage from './OptimizedImage';

const FeaturedSection = styled.section`
  padding: 80px 0;
  background: white;
  width: 100%;
`;

const Container = styled.div`
  width: 100%;
  margin: 0;
  padding: 0;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 50px;
  padding: 0 20px;

  h2 {
    font-size: 2.5rem;
    color: #333;
    margin-bottom: 15px;
    font-weight: 300;
  }

  p {
    font-size: 1.1rem;
    color: #666;
    max-width: 600px;
    margin: 0 auto;
  }
`;

const ProductsContainer = styled.div`
  position: relative;
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  padding: 0;
  
  /* Scrollbar - site paletiyle uyumlu (marka rengi #1a1a1a) */
  scrollbar-width: thin;
  scrollbar-color: #1a1a1a #f0ece8;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f0ece8;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #1a1a1a;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #000000;
  }
`;

const ProductsScroll = styled.div`
  display: flex;
  gap: 40px;
  padding: 0 40px;
  width: max-content;
  min-width: 100%;

  @media (max-width: 768px) {
    gap: 20px;
    padding: 0 20px;
  }
`;

const ProductCard = styled.div`
  background: white;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  width: 400px;
  min-width: 400px;
  flex-shrink: 0;
  cursor: pointer;
  position: relative;

  /* 400px sabit genislik mobilde ekrandan tasip urun adini kesiyordu.
     Ekranin ~%78'i kadar yapinca yanindaki kart da goruniyor,
     boylece yatay kaydirilabilecegi anlasiliyor. */
  @media (max-width: 768px) {
    width: 78vw;
    min-width: 78vw;
  }

  &:hover {
    transform: translateY(-5px);
  }
`;

const ProductImageContainer = styled.div`
  height: 550px;

  @media (max-width: 768px) {
    height: 104vw;
  }
  position: relative;
  overflow: hidden;
  background: #f0f0f0;
`;

const ProductImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const HoverImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  transition: opacity 0.5s ease;
  z-index: 1;
  pointer-events: none;
  
  ${ProductImageContainer}:hover & {
    opacity: 1;
  }
`;

const GradientOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.1));
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 2;
  pointer-events: none;
  
  ${ProductImageContainer}:hover & {
    opacity: 1;
  }
`;

const ProductBadges = styled.div`
  position: absolute;
  top: 15px;
  left: 15px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Badge = styled.span<{ type: 'sale' | 'featured' | 'new' | 'soldout' }>`
  padding: 7px 14px;
  border-radius: 2px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  line-height: 1;
  backdrop-filter: blur(6px);

  ${props => {
    switch (props.type) {
      case 'sale':
        return 'background: rgba(255, 255, 255, 0.94); color: #b3242f; border: 1px solid rgba(179, 36, 47, 0.35);';
      case 'featured':
        return 'background: #1a1a1a; color: #ffffff; border: 1px solid #1a1a1a;';
      case 'new':
        return 'background: rgba(255, 255, 255, 0.94); color: #1a1a1a; border: 1px solid rgba(26, 26, 26, 0.25);';
      case 'soldout':
        return 'background: rgba(26, 26, 26, 0.82); color: #ffffff; border: 1px solid rgba(26, 26, 26, 0.82);';
      default:
        return 'background: rgba(255, 255, 255, 0.94); color: #1a1a1a; border: 1px solid rgba(26, 26, 26, 0.15);';
    }
  }}
`;

const ProductContent = styled.div`
  padding: 20px;
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
`;

const ProductName = styled.h3`
  font-family: 'Caviar Dreams', sans-serif;
  font-size: 1.1rem;
  color: #2c2c2c;
  margin: 0;
  font-weight: bold;
  line-height: 1.3;
  letter-spacing: -0.02em;
  text-align: center;
`;

const ProductPrice = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CurrentPrice = styled.span<{ $onSale?: boolean }>`
  font-size: 0.95rem;
  font-weight: 700;
  color: ${props => props.$onSale ? '#ff4757' : '#333'};
  letter-spacing: -0.02em;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 60px 0;
`;

const LoadingIcon = styled.div`
  font-size: 3rem;
  color: #333;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 0;
  
  h3 {
    font-size: 1.5rem;
    color: #666;
    margin-bottom: 15px;
  }
  
  p {
    color: #999;
    font-size: 1rem;
  }
`;

const FeaturedProductsSection: React.FC = () => {
  const navigate = useNavigate();
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFeaturedProducts();
  }, []);

  const loadFeaturedProducts = async () => {
    setLoading(true);
    try {
      // Sadece gelinlik kategorisindeki öne çıkan ürünleri getir
      const result = await ProductsService.getProductsByCategory('gelinlik');
      if (result.success && result.data) {
        // Sadece öne çıkan ürünleri filtrele
        const featuredGelinlikler = result.data.filter(product => product.isFeatured);
        setFeaturedProducts(featuredGelinlikler);
      }
    } catch (error) {
      console.error('Error loading featured products:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <FeaturedSection>
        <Container>
          <LoadingState>
            <LoadingIcon>⏳</LoadingIcon>
          </LoadingState>
        </Container>
      </FeaturedSection>
    );
  }

  if (featuredProducts.length === 0) {
    return (
      <FeaturedSection>
        <Container>
          <SectionHeader>
            <h2>Öne Çıkan Gelinlikler</h2>
            <p>En beğenilen gelinlik tasarımlarımız</p>
          </SectionHeader>
          <EmptyState>
            <h3>Henüz öne çıkan ürün yok</h3>
            <p>Yakında en güzel tasarımlarımızla sizlerle buluşacağız.</p>
          </EmptyState>
        </Container>
      </FeaturedSection>
    );
  }

  return (
    <FeaturedSection>
      <Container>
        <SectionHeader>
          <h2>Öne Çıkan Gelinlikler</h2>
          <p>En beğenilen gelinlik tasarımlarımız</p>
        </SectionHeader>

        <ProductsContainer>
          <ProductsScroll>
            {featuredProducts.map(product => (
              <ProductCard key={product.id} onClick={() => {
                console.log('Product clicked:', product.name);
                // Use product name in URL (encoded for special characters)
                const encodedName = encodeURIComponent(product.name);
                navigate(`/gelinlikler/${encodedName}`);
              }}>
                <ProductImageContainer>
                  <ProductImageWrapper>
                    <OptimizedImage
                      src={product.images[0] || '/pictures/gelinlik/gelinlik.webp'}
                      alt={product.name}
                      fill
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 400px"
                      quality={75}
                      loading="lazy"
                      objectFit="cover"
                    />
                    {product.images[1] && (
                      <HoverImage
                        src={product.images[1]}
                        alt={`${product.name} - Hover`}
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 400px"
                        loading="lazy"
                      />
                    )}
                    <GradientOverlay />
                  </ProductImageWrapper>
                  <ProductBadges>
                    {product.stock === 0 && <Badge type="soldout">Stokta Yok</Badge>}
                  {product.isOnSale && <Badge type="sale">İndirim</Badge>}
                    {product.isFeatured && <Badge type="featured">Öne Çıkan</Badge>}
                    {new Date().getTime() - product.createdAt.getTime() < 7 * 24 * 60 * 60 * 1000 && (
                      <Badge type="new">Yeni</Badge>
                    )}
                  </ProductBadges>
                </ProductImageContainer>
                
                <ProductContent>
                  <ProductName>{product.name}</ProductName>
                  <ProductPrice>
                    <CurrentPrice $onSale={product.isOnSale}>
                      ₺{product.price.toLocaleString()}
                    </CurrentPrice>
                  </ProductPrice>
                </ProductContent>
              </ProductCard>
            ))}
          </ProductsScroll>
        </ProductsContainer>
      </Container>
    </FeaturedSection>
  );
};

export default FeaturedProductsSection;
